import React, { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styled from "@emotion/styled";
import CompareArrowsIcon from "@icons/myicons/windows-compare.svg?react";
import { ComparisonValue } from "../types";

const MenuItemStyled = styled(MenuItem)<{ type?: string }>(
  ({ theme, type }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: "412px",
    width: "100%",
    color:
      type === "recommendation"
        ? theme.palette.primary.dark
        : theme.palette.text.primary,
    "&.Mui-selected": {
      backgroundColor: "unset",
    },
  })
);

const ButtonStyled = styled(Button)(({ fullWidth }) => ({
  minWidth: 0,
  width: fullWidth ? undefined : "300px",
  maxWidth: "100%",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    flexShrink: 0,
  },
}));

const TruncatedTypography = styled(Typography)({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export interface SelectComparisonProps {
  value: string | null;
  values: ComparisonValue[];
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onChange: (ticker: string | null) => void;
}

export const SelectComparison = ({
  value,
  values = [],
  className,
  disabled,
  fullWidth,
  onChange,
}: SelectComparisonProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleComparedValueChange = (comparisonValue: string) => {
    onChange(comparisonValue);
    handleClose();
  };

  const handleClearComparedValue = (event: MouseEvent<Element>) => {
    event.stopPropagation();
    onChange(null);
    handleClose();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const currentValue = values.find((item) => item.value === value);

  return (
    <>
      <ButtonStyled
        className={className}
        size="small"
        variant="outlined"
        onClick={handleOpenMenu}
        disabled={disabled}
        fullWidth={fullWidth}
        startIcon={<CompareArrowsIcon />}
        endIcon={
          currentValue ? (
            <ClearIcon
              onClick={(event: MouseEvent<SVGSVGElement>) =>
                handleClearComparedValue(event)
              }
            />
          ) : (
            <ExpandMoreIcon />
          )
        }
      >
        <Typography variant="body3" mr={currentValue ? 1 : 0}>
          {t("general.chart.selectComparison.compareWith")}
          {currentValue && ":"}
        </Typography>
        <TruncatedTypography
          variant="body3"
          color={
            currentValue?.type === "recommendation"
              ? theme.palette.primary.dark
              : theme.palette.text.primary
          }
        >
          {currentValue && currentValue.label}
        </TruncatedTypography>
      </ButtonStyled>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handleClose}
        disablePortal
      >
        <MenuItemStyled
          disabled={!value}
          onClick={(event: React.MouseEvent<HTMLLIElement>) =>
            handleClearComparedValue(event)
          }
        >
          Clear
        </MenuItemStyled>

        {values.map((comparisonValue) => (
          <MenuItemStyled
            type={comparisonValue.type}
            key={comparisonValue.value}
            onClick={() => handleComparedValueChange(comparisonValue.value)}
          >
            <Typography>{comparisonValue.label}</Typography>
          </MenuItemStyled>
        ))}
      </Menu>
    </>
  );
};
